/**
 * API 에러 파서
 */
import { AxiosError } from "axios";

/**
 * 에러 타입 정의
 */
export enum ErrorType {
    NETWORK_ERROR = 'NETWORK_ERROR',
    UNAUTHORIZED = 'UNAUTHORIZED',
    FORBIDDEN = 'FORBIDDEN',
    NOT_FOUND = 'NOT_FOUND',
    BAD_REQUEST = 'BAD_REQUEST',
    SERVER_ERROR = 'SERVER_ERROR',
    TIMEOUT = 'TIMEOUT',
    UNKNOWN = 'UNKNOWN',
}

/**
 * 파싱된 에러 인터페이스
 */
export interface ParsedError extends Error {
    type: ErrorType;
    statusCode?: number;
    originalError: unknown;
}

/**
 * Axios 에러를 파싱하여 표준화된 에러로 변환
 */
export const parseError = (error: unknown): ParsedError => {
    // Axios 에러인 경우
    if (isAxiosError(error)) {
        const axiosError = error as AxiosError;

        // 응답이 있는 경우 (서버에서 응답을 받았으나 에러 코드)
        if (axiosError.response) {
            const statusCode = axiosError.response.status;
            const errorMessage =
                (axiosError.response.data as { message?: string })?.message ||
                axiosError.message;

            return {
                name: 'ApiError',
                message: errorMessage,
                type: mapStatusToErrorType(statusCode),
                statusCode,
                originalError: error,
            };
        }

        // 요청은 보냈으나 응답을 받지 못한 경우
        if (axiosError.request) {
            return {
                name: 'NetworkError',
                message: '네트워크 연결을 확인해주세요',
                type: ErrorType.NETWORK_ERROR,
                originalError: error,
            };
        }

        // 타임아웃 에러
        if (axiosError.code === 'ECONNABORTED') {
            return {
                name: 'TimeoutError',
                message: '요청 시간이 초과되었습니다',
                type: ErrorType.UNKNOWN,
                originalError: error,
            };
        }
    }

    // 일반 Error 객체인 경우
    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
            type: ErrorType.UNKNOWN,
            originalError: error,
        };
    }

    // 알 수 없는 에러
    return {
        name: 'UnknownError',
        message: '알 수 없는 오류가 발생했습니다',
        type: ErrorType.UNKNOWN,
        originalError: error,
    };
}

/**
 * HTTP 상태 코드를 ErrorType으로 매핑
 */
const mapStatusToErrorType = (statusCode: number): ErrorType => {
    switch (statusCode) {
        case 400:
            return ErrorType.BAD_REQUEST;
        case 401:
            return ErrorType.UNAUTHORIZED;
        case 403:
            return ErrorType.FORBIDDEN;
        case 404:
            return ErrorType.NOT_FOUND;
        case 408:
            return ErrorType.TIMEOUT;
        case 500:
        case 502:
        case 503:
        case 504:
            return ErrorType.SERVER_ERROR;
        default:
            return ErrorType.UNKNOWN;
    }
};

/**
 * Axios 에러 타입 가드
 */
const isAxiosError = (error: unknown): error is AxiosError => {
    return (error as AxiosError).isAxiosError === true;
};
